import {
	FieldErrors,
	FieldValues,
	UseFormHandleSubmit,
	UseFormRegister,
	UseFormSetValue,
} from 'react-hook-form';

import {
	GridContainer,
	Input,
	SpinnerOverlay,
	SubmissionButtons,
} from '@components/common';

import { useModal } from '@components/common/Modal/utils';

import { regExpDecimal, regExpLetters, regExpSingleDecimalFloat } from '@constants/regExp';
import {
	CHANNEL_BANDWIDTH_TOOLTIP,
	ERUConfigurationFormFields,
	ERUConfigurationFormTabNames,
	RU_CARRIER_DATA_FORM_AREAS,
	TRANSMISSION_GAIN_TOOLTIP,
} from '../constants';

import styles from '../RUConfigurationFormTabs.module.scss';

interface IRUCarriersDataFormProps {
	errors: FieldErrors<FieldValues>;
	isLoading: boolean;
	isEditMode: boolean;
	isSubmitDisabled: boolean;
	setCurrentTab: (tab: string) => void;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	handleSubmit: UseFormHandleSubmit<FieldValues, undefined>;
	submitForm: (data: FieldValues) => void;
}

const RUCarriersDataForm = ({
	errors,
	isLoading,
	isSubmitDisabled,
	isEditMode,
	setCurrentTab,
	register,
	setValue,
	handleSubmit,
	submitForm,
}: IRUCarriersDataFormProps) => {
	const { onClose } = useModal();

	const negativeHandler = () => {
		if (isEditMode) {
			onClose();
		} else {
			setCurrentTab(ERUConfigurationFormTabNames.configuration);
		}
	};

	const positiveHandler = (values: FieldValues) => {
		submitForm(values);
	};

	return (
		<GridContainer
			columns={2}
			customClassName={styles.ruConfigurationFormContainer}
			gap='15px 20px'
			gridTemplateAreas={RU_CARRIER_DATA_FORM_AREAS}
		>
			{isLoading && <SpinnerOverlay/>}
			<Input
				containerClassName={styles.mimoMode}
				disabled
				errorMessage={errors?.mimoMode?.message}
				label='MIMO mode'
				name={ERUConfigurationFormFields.mimoMode}
				placeholder='Enter MIMO mode'
				regexValidation={regExpLetters}
				register={register}
				required
				setValue={setValue}
			/>
			<Input
				containerClassName={styles.frequency}
				errorMessage={errors?.frequency?.message}
				infoTooltip={
					<div>
						<span className={styles.tooltipPadding}>Center</span>
						frequency of channel bandwidth in Hz. Common for all numerologies.
						<br/>
						<span className={styles.tooltipPadding}>center-of-channel-bandwidth</span>
						is the FREF translated from absolute-frequency-center
						(NREF) as per 3GPP TS 38.104.
					</div>
				}
				label='Frequency (Hz)'
				name={ERUConfigurationFormFields.frequency}
				placeholder='Enter frequency'
				regexValidation={regExpSingleDecimalFloat}
				register={register}
				required
				setValue={setValue}
			/>
			<Input
				containerClassName={styles.nrafrcn}
				errorMessage={errors?.nrafrcn?.message}
				infoTooltip={
					<div>
						<span className={styles.tooltipPadding}>This</span>
						provides value of freqOffset to be used if section type 3 is not used.
						See freqOffset in CUS-plane.
						<span className={styles.tooltipPadding}>offset-to-absolute-frequency-center</span>
						defines the relative spacing between the center
						of RE#0 of RB#0 to the center-of-channel-bandwidth.
						<br/>
						<span className={styles.tooltipPadding}>If </span>
						offset-to-absolute-frequency-center for NR with SCS=15kHz is odd, it implies that 7.5kHz shift
						as indicated by the parameter frequencyShift7p5khz for UL is applied.
						<span className={styles.tooltipPadding}>When </span>
						type (in tx/rx-array-carrier) is DSS, “offset-to-absolute-frequency-center”
						<br/>
					</div>
				}
				label='NR ARFCN'
				name={ERUConfigurationFormFields.nrafrcn}
				placeholder='Enter NR ARFCN'
				regexValidation={regExpSingleDecimalFloat}
				register={register}
				required
				setValue={setValue}
			/>
			<Input
				containerClassName={styles.channelBandwidth}
				errorMessage={errors?.channelBandwidth?.message}
				infoTooltip={CHANNEL_BANDWIDTH_TOOLTIP}
				label='Channel Bandwidth (Hz)'
				name={ERUConfigurationFormFields.channelBandwidth}
				placeholder='Enter channel bandwidth'
				regexValidation={regExpSingleDecimalFloat}
				register={register}
				required
				setValue={setValue}
			/>
			<Input
				containerClassName={styles.transmissionGain}
				errorMessage={errors?.transmissionGain?.message}
				infoTooltip={TRANSMISSION_GAIN_TOOLTIP}
				label='Transmission Gain (dB)'
				name={ERUConfigurationFormFields.transmissionGain}
				placeholder='Enter transmission gain'
				regexValidation={regExpDecimal}
				register={register}
				required
				setValue={setValue}
			/>
			<SubmissionButtons
				className={styles.submitButtons}
				disabledPositive={isSubmitDisabled}
				negativeHandler={negativeHandler}
				negativeLabel={isEditMode ? 'Cancel' : 'Back'}
				positiveHandler={handleSubmit(positiveHandler)}
				positiveLabel='Save and update'
			/>
		</GridContainer>
	);
};

export default RUCarriersDataForm;
